/** Wrapper around XMLHttpRequest that automatically handles auth and Omni routing (ident path) */
import { state, IS_CLOUD_MODE } from './deluge.js';

function splitDetail(detail) {
  if (typeof detail === 'string') {
    return detail.split('|||');
  }
  if (Array.isArray(detail)) {
    return detail.map((d) => (typeof d === 'object' ? d.msg || JSON.stringify(d) : String(d)));
  }
  return [String(detail)];
}

function logDetail(detail, type) {
  const detailsArray = splitDetail(detail);

  let delay = 5000;

  for (const subDetail of detailsArray) {
    state.notifications.push({
      type: type,
      msg: subDetail,
    });
    setTimeout(() => {
      state.notifications.shift();
    }, delay);
    delay += 5000;
  }
}

export const $http = (
  method,
  url,
  data = null,
  timeout = 10 * 1000,
  no_prepend = false
) => {
  return new Promise((resolve, reject) => {
    var xhr = new XMLHttpRequest();
    xhr.timeout = timeout; // time in milliseconds]
    // If no_prepend is set we want to leave the URL exactly as provided
    // (this is used for paths like '/upload/...' which Caddy will proxy to the upload server).
    if (no_prepend) {
      // On-prem: Caddy proxies /upload/* to the upload server on the same
      // origin, so we leave the URL untouched. In cloud mode the page is
      // hosted on canvaspro-cloud.pages.dev but /upload/* lives on the
      // worker, so we still need to prepend devRedirect (without the /api
      // segment, since /upload/* is not under /api).
      if (IS_CLOUD_MODE && state.devRedirect && !/^https?:/.test(url)) {
        url = state.devRedirect + url;
      }
    } else {
      // Prepend any session ident (used for Omni/app-scoped routes) then the API prefix
      if (sessionStorage.getItem('ident')) {
        url = sessionStorage.getItem('ident') + url;
      }
      // strip everything between /apps/ and /dist/ https://127.0.0.1:33333/apps/sports/ui/dist/api/
      // check if /apps/*/ui/dist/ is in the current domain path
      // NOTE: the leading slash on `/api` is important. Older code wrote
      // `state.devRedirect + 'api' + url`, which depends on the devRedirect
      // being empty (the browser then treats `'api/...'` as relative). In
      // cloud mode devRedirect is an absolute URL like `https://worker.dev`,
      // so we need the explicit separator to avoid `…worker.devapi/...`.
      url = state.devRedirect + '/api' + url;
      if (window.location.pathname.includes('/apps/')) {
        url = '/' + url;
      }
    }

    xhr.open(method, url, true);
    xhr.setRequestHeader(
      'Authorization',
      'Bearer ' + localStorage.getItem('authentication_token')
    );
    // console.log(
    //   'sending token',
    //   localStorage.getItem('authentication_token', 'for', url)
    // );
    xhr.onload = function () {
      if (this.status >= 200 && this.status < 300) {
        try {
          if (method !== 'GET') {
            const response = JSON.parse(xhr.response);
            if (response?.detail) {
              state.notifications.push({
                type: 'info',
                msg: response.detail,
              });
              setTimeout(() => {
                state.notifications.shift();
              }, 3000);
            }
          }
        } catch (e) {
          console.error('error parsing response', e);
        }
        resolve(xhr.response);
      } else if (this.status === 401) {
        localStorage.setItem('authentication_token', undefined);
      } else {
        if (xhr.response) {
          try {
            const response = JSON.parse(xhr.response);
            if (response.detail) {
              logDetail(response.detail, 'error');
            }
          } catch (e) {
            console.error(
              'successful response but out side of expected scope',
              e
            );
          }
        }

        reject({
          status: this.status,
          statusText: xhr.statusText,
          response: xhr.response,
        });
      }
    };
    xhr.onerror = function () {
      // if response in json format and contains details property

      console.log('error');

      if (xhr.response) {
        try {
          const response = JSON.parse(xhr.response);
          if (response.detail) {
            logDetail(response.detail, 'error');
          }
        } catch (e) {
          console.error('error parsing response', e);
        }
      }

      reject({
        status: this.status,
        statusText: xhr.statusText,
      });
    };
    if (data) {
      if (typeof data === 'string') {
        // stringified json
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('accept', 'application/json');
      }

      xhr.send(data);
    } else {
      xhr.send();
    }
  });
};

/**
 * POST a File/Blob as the raw request body with upload progress events.
 *
 * Drives the existing BottomBar upload progress widget via state.upload_*
 * fields (uploading / total / current_index / current_filename / progress /
 * bytes_done / bytes_total). Useful for endpoints that consume the raw
 * body — e.g. /api/dynamic_engine/prepare_*_archive — where we want a
 * progress bar but don't go through the chunked Upload.vue pipeline.
 *
 * Returns the parsed JSON response. Throws an Error on non-2xx with the
 * server's `detail` if available.
 */
export async function uploadRawBodyWithProgress(url, file, options = {}) {
  const { contentType = 'application/octet-stream' } = options;
  const token = localStorage.getItem('authentication_token') ?? '';

  // Show the BottomBar widget for the duration of this single-file upload.
  state.uploading = true;
  state.upload_total = 1;
  state.upload_current_index = 1;
  state.upload_current_filename = file.name;
  state.upload_progress = 0;
  state.upload_bytes_total = file.size;
  state.upload_bytes_done = 0;
  state.upload_eta = '';
  const startedAt = Date.now();

  try {
    return await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);
      xhr.setRequestHeader('Authorization', 'Bearer ' + token);
      xhr.setRequestHeader('Content-Type', contentType);
      xhr.responseType = 'text';

      xhr.upload.onprogress = (e) => {
        if (!e.lengthComputable) return;
        state.upload_bytes_done = e.loaded;
        state.upload_progress = Math.min(100, (e.loaded / e.total) * 100);
        const elapsed = (Date.now() - startedAt) / 1000;
        if (elapsed > 0.5 && e.loaded > 0) {
          const bytesPerSec = e.loaded / elapsed;
          const remaining = (e.total - e.loaded) / bytesPerSec;
          if (Number.isFinite(remaining) && remaining > 0) {
            state.upload_eta = remaining > 60
              ? `${Math.round(remaining / 60)}m left`
              : `${Math.round(remaining)}s left`;
          }
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(xhr.response ? JSON.parse(xhr.response) : null);
          } catch (e) {
            reject(new Error(`Could not parse response: ${e}`));
          }
        } else {
          let detail = xhr.statusText;
          try {
            const parsed = JSON.parse(xhr.response);
            if (parsed?.detail) detail = parsed.detail;
          } catch {
            /* keep statusText */
          }
          reject(new Error(detail));
        }
      };
      xhr.onerror = () => reject(new Error('Network error during upload'));
      xhr.onabort = () => reject(new Error('Upload aborted'));

      xhr.send(file);
    });
  } finally {
    state.uploading = false;
    state.upload_current_index = 0;
    state.upload_total = 0;
    state.upload_progress = 0;
    state.upload_bytes_done = 0;
    state.upload_bytes_total = 0;
    state.upload_current_filename = '';
    state.upload_eta = '';
  }
}
